import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';
import { PERSONA_CARDS } from '@/data/personas';

export default function PersonaSelector() {
  const navigate = useNavigate();
  const setPersona = useSessionStore((s) => s.setPersona);
  const loadFixtures = useBrainStore((s) => s.loadFixtures);
  const initialised = useBrainStore((s) => s.initialised);

  useEffect(() => {
    if (!initialised) loadFixtures();
  }, [initialised, loadFixtures]);

  return (
    <div className="min-h-screen w-full bg-ink-black text-cream flex flex-col">
      <header className="px-12 pt-16 pb-10 flex flex-col gap-2 max-w-6xl mx-auto w-full">
        <img
          src="/logo/tattoo_vault_wordmark.png"
          alt="Tattoo Vault"
          className="h-20 w-auto self-start object-contain"
          data-testid="persona-wordmark"
        />
        <h1 className="heading-display text-4xl md:text-6xl text-gold-bright">
          A working prototype.
        </h1>
        <p className="font-serif text-lg text-cream-soft mt-3">
          Chaos out. Clarity in. Pick a seat at the table and step into Monday morning.
        </p>
      </header>

      <main className="px-12 pb-16 grow">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PERSONA_CARDS.map((card, idx) => (
            <motion.button
              key={card.persona + idx}
              data-testid={`persona-card-${card.persona}`}
              type="button"
              onClick={() => {
                setPersona(card.persona, {
                  agentId: card.agentId,
                  artistId: card.artistId,
                  apprenticeId: card.apprenticeId,
                  assignedStudios: card.assignedStudios,
                  activeStudio: card.activeStudio,
                });
                navigate(card.destination);
              }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05, duration: 0.3 }}
              whileHover={{ y: -4 }}
              className="text-left bg-ink-deep border border-gold-dim/30 rounded-2xl p-7 hover:border-gold-bright hover:shadow-gold-glow transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright"
            >
              <p className="label-small-caps text-gold-bright">{card.role}</p>
              <h2 className="heading-serif text-2xl text-cream mt-3">{card.name}</h2>
              <p className="font-inter text-sm text-cream-soft leading-relaxed mt-4">
                {card.description}
              </p>
              <p className="mt-6 font-inter text-xs text-gold uppercase tracking-widest">
                Begin at {card.defaultHome}
              </p>
            </motion.button>
          ))}
        </div>
      </main>

      <footer className="px-12 pb-8 max-w-6xl mx-auto w-full">
        <div className="divider-gold mb-6" />
        <p className="font-inter text-xs text-cream-soft/70 tracking-wider uppercase">
          Simulated. Mock-everything. Stripe in test mode only. Monday 04 May 2026 at 8:42am.
        </p>
      </footer>
    </div>
  );
}
